export const PHASE_INTEGRATION = 'integration';
export const PHASE_ACTIFS = 'actifs';
export const PHASE_RESILIATION = 'resiliation';

export const LIFECYCLE_PHASES = [
  { code: PHASE_INTEGRATION, label: "En cours d'intégration", shortLabel: 'Intégration' },
  { code: PHASE_ACTIFS, label: 'Actifs', shortLabel: 'Actifs' },
  { code: PHASE_RESILIATION, label: 'Résiliation', shortLabel: 'Résiliation' },
];

export const PHASE_LABELS = Object.fromEntries(
  LIFECYCLE_PHASES.map((phase) => [phase.code, phase.label]),
);

const GROUP_META = {
  vente: { label: 'Process de vente', phase: PHASE_INTEGRATION },
  fabrication: { label: 'Process de fabrication', phase: PHASE_INTEGRATION },
  mise_en_ligne: { label: 'Mise en ligne', phase: PHASE_ACTIFS },
  resiliation: { label: 'Résiliation', phase: PHASE_RESILIATION },
  commercial: { label: 'Commercial', phase: PHASE_INTEGRATION },
  ce_catherine: { label: 'CE', phase: PHASE_INTEGRATION },
  presta_photo: { label: 'Presta photo', phase: PHASE_INTEGRATION },
  site_klub: { label: 'Site Klub', phase: PHASE_INTEGRATION },
};

const STEP_PHASE_MAP = {
  signe: PHASE_INTEGRATION,
  rdv_fab: PHASE_INTEGRATION,
  valide: PHASE_INTEGRATION,
  questionnaire_emis: PHASE_INTEGRATION,
  questionnaire_transmis: PHASE_INTEGRATION,
  questionnaire_complete: PHASE_INTEGRATION,
  photos_recues: PHASE_INTEGRATION,
  site_a_creer: PHASE_INTEGRATION,
  bat_emis: PHASE_INTEGRATION,
  bat_valide: PHASE_INTEGRATION,
  ras: PHASE_ACTIFS,
  resiliation_demandee: PHASE_RESILIATION,
  resiliation_programmee: PHASE_RESILIATION,
  resiliation_effective: PHASE_RESILIATION,
  commande_validee: PHASE_INTEGRATION,
  questionnaire_edite: PHASE_INTEGRATION,
  questionnaire_envoye: PHASE_INTEGRATION,
  presta_prevenue: PHASE_INTEGRATION,
  questionnaire_recu: PHASE_INTEGRATION,
  liste_salaries: PHASE_INTEGRATION,
  rdv_photo_planifie: PHASE_INTEGRATION,
  rdv_photo_realise: PHASE_INTEGRATION,
  site_redige: PHASE_INTEGRATION,
  photos_site_integres: PHASE_INTEGRATION,
  site_relu: PHASE_INTEGRATION,
  modif_validation_recues: PHASE_INTEGRATION,
  modifs_effectuees: PHASE_INTEGRATION,
  mise_en_ligne: PHASE_ACTIFS,
  cartes_fabriquees: PHASE_ACTIFS,
  cartes_remises: PHASE_ACTIFS,
};

const STATUT_PHASE_MAP = {
  en_cours: PHASE_INTEGRATION,
  signe_sans_rdv: PHASE_INTEGRATION,
  actif: PHASE_ACTIFS,
  resilie: PHASE_RESILIATION,
};

const PHASE_DEFAULT_GROUP = {
  [PHASE_INTEGRATION]: 'vente',
  [PHASE_ACTIFS]: 'mise_en_ligne',
  [PHASE_RESILIATION]: 'resiliation',
};

export function isChecklistStep(stepCode) {
  return Boolean(stepCode) && stepCode !== 'signe';
}

export function isStepDone(status) {
  return status === 'ok' || status === 'na';
}

export function groupLabel(code) {
  if (code === 'other') return 'Autres';
  return GROUP_META[code]?.label ?? code;
}

export function groupPhase(code) {
  return GROUP_META[code]?.phase ?? PHASE_INTEGRATION;
}

export function phaseFromStatut(statut) {
  return STATUT_PHASE_MAP[statut] ?? PHASE_INTEGRATION;
}

export function stepFilterLabel(step, { includePhase = true } = {}) {
  const label = step.label ?? step.step_label ?? step.step_code;
  if (!includePhase) return label;
  const phase = phaseForStep(step.step_code, step.display_group);
  const phaseMeta = LIFECYCLE_PHASES.find((item) => item.code === phase);
  return phaseMeta ? `${phaseMeta.shortLabel} · ${label}` : label;
}

export function phaseForStep(stepCode, displayGroup = null) {
  if (stepCode && STEP_PHASE_MAP[stepCode]) {
    return STEP_PHASE_MAP[stepCode];
  }
  if (displayGroup && GROUP_META[displayGroup]) {
    return GROUP_META[displayGroup].phase;
  }
  return PHASE_INTEGRATION;
}

export function normalizeDisplayGroup(stepCode, displayGroup = null) {
  const phase = phaseForStep(stepCode, displayGroup);
  if (!displayGroup || !GROUP_META[displayGroup] || GROUP_META[displayGroup].phase !== phase) {
    return PHASE_DEFAULT_GROUP[phase];
  }
  return displayGroup;
}

export function phaseState(currentPhase, phaseCode) {
  const order = LIFECYCLE_PHASES.map((phase) => phase.code);
  const currentIndex = order.indexOf(currentPhase);
  const index = order.indexOf(phaseCode);
  if (currentIndex < 0 || index < 0) return 'upcoming';
  if (index < currentIndex) return 'done';
  if (index === currentIndex) return 'current';
  return 'upcoming';
}

export function groupStepsByPhase(steps = []) {
  const buckets = Object.fromEntries(
    LIFECYCLE_PHASES.map((phase) => [phase.code, []]),
  );

  [...steps]
    .sort((a, b) => (a.step_order ?? 0) - (b.step_order ?? 0))
    .forEach((step) => {
      const phase = step.lifecycle_phase
        ?? phaseForStep(step.step_code, step.display_group);
      if (!buckets[phase]) {
        buckets[phase] = [];
      }
      buckets[phase].push({
        ...step,
        lifecycle_phase: phase,
        display_group: normalizeDisplayGroup(step.step_code, step.display_group),
      });
    });

  return buckets;
}

export function groupStepsByProcess(steps = []) {
  const groups = [];
  const indexByCode = {};

  [...steps]
    .sort((a, b) => (a.step_order ?? 0) - (b.step_order ?? 0))
    .forEach((step) => {
      const code = step.display_group || 'other';
      if (indexByCode[code] === undefined) {
        indexByCode[code] = groups.length;
        groups.push({
          code,
          label: groupLabel(code),
          steps: [],
        });
      }
      groups[indexByCode[code]].steps.push(step);
    });

  return groups;
}

export function countStepProgress(steps = [], statuses = {}) {
  const total = steps.length;
  const ok = steps.filter((step) => {
    const status = step.status ?? statuses[step.step_code] ?? 'pending';
    return status === 'ok' || status === 'na';
  }).length;
  const blocked = steps.some((step) => {
    const status = step.status ?? statuses[step.step_code] ?? 'pending';
    return status === 'blocked';
  });

  return { ok, total, blocked };
}

export const PHASE_FILTER_OPTIONS = LIFECYCLE_PHASES.map((phase) => ({
  value: phase.code,
  label: phase.label,
}));

export const PREAVIS_BUCKETS = [
  { code: 'urgent', label: 'Préavis urgent', hint: 'Date limite de préavis dans moins de 10 jours' },
  { code: 'imminent', label: 'Préavis imminent', hint: 'Date limite de préavis dans moins de 30 jours' },
  { code: 'ras', label: 'RAS', hint: 'Date limite de préavis dans plus de 30 jours' },
  { code: 'depasse', label: 'Préavis dépassé', hint: 'Renouvellement dans moins de 60 jours' },
];

export const PREAVIS_FILTER_OPTIONS = PREAVIS_BUCKETS.map((bucket) => ({
  value: bucket.code,
  label: bucket.label,
}));

export const PREAVIS_BADGE_CLASS = {
  depasse: 'bg-danger-focus text-danger-main',
  urgent: 'bg-warning-focus text-warning-main',
  imminent: 'bg-info-focus text-info-main',
  ras: 'bg-success-focus text-success-main',
};

function parseDateOnly(value) {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const [year, month, day] = value.slice(0, 10).split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

function dateForMonthDay(year, monthIndex, day) {
  const candidate = new Date(year, monthIndex, day);
  if (candidate.getMonth() !== monthIndex) {
    return new Date(year, monthIndex + 1, 0);
  }
  return candidate;
}

export function nextRenewalAnniversary(dateStr, today = new Date()) {
  const renewal = parseDateOnly(dateStr);
  if (!renewal) return null;
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  let candidate = dateForMonthDay(start.getFullYear(), renewal.getMonth(), renewal.getDate());
  if (candidate < start) {
    candidate = dateForMonthDay(start.getFullYear() + 1, renewal.getMonth(), renewal.getDate());
  }
  return candidate;
}

export function daysUntilNextAnniversary(dateStr, today = new Date()) {
  const next = nextRenewalAnniversary(dateStr, today);
  if (!next) return null;
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((next.getTime() - start.getTime()) / 86400000);
}

function toIsoDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function nextRenewalIso(dateStr, today = new Date()) {
  const next = nextRenewalAnniversary(dateStr, today);
  return next ? toIsoDate(next) : null;
}

/** Date limite de préavis = prochain anniversaire de renouvellement − 60 jours. */
export function preavisDeadlineIso(dateStr, today = new Date()) {
  const next = nextRenewalAnniversary(dateStr, today);
  if (!next) return null;
  const deadline = new Date(next.getFullYear(), next.getMonth(), next.getDate() - 60);
  return toIsoDate(deadline);
}

export function preavisFromDate(dateStr, today = new Date()) {
  const days = daysUntilNextAnniversary(dateStr, today);
  if (days == null) return 'ras';
  if (days <= 60) return 'depasse';
  if (days <= 70) return 'urgent';
  if (days <= 90) return 'imminent';
  return 'ras';
}

export function preavisLabel(code) {
  return PREAVIS_BUCKETS.find((bucket) => bucket.code === code)?.label ?? code;
}

/** Texte relatif au seuil de 60 jours (date limite de préavis = anniversaire − 60 j). */
export function preavisTimingLabel(dateStr, today = new Date()) {
  const days = daysUntilNextAnniversary(dateStr, today);
  if (days == null) return 'Sans date de renouvellement';
  const code = preavisFromDate(dateStr, today);
  if (code === 'depasse') {
    return `depuis ${60 - days} j`;
  }
  return `dans ${Math.max(0, days - 60)} j`;
}
